import React, { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, Modal } from "react-bootstrap";

// Components
import Spinner from "../Components/Spinner";

// Services
import { usersService } from "../../Services/users.service";
import { clientUserService } from "../../Services/clientUsers.service";
import { storage } from "../../Services/storage.service";
import { storageKeys } from "../../Constants/storage.key";
import { paginationService } from "../../Services/pagination.service";
import { SiAddthis } from "react-icons/si";
import ToastError from "../Components/ToastError";
import DeleteModal from "../Components/Modals/Delete-modal";
import { BiEditAlt, BiTrash } from "react-icons/bi";
import { FaCar, FaCheck, FaTimes } from "react-icons/fa";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
} from "@syncfusion/ej2-react-grids";
import EmptyData from "../../components/EmptyData";

const formInitialValues = {
  userId: 0,
  userName: "",
  userEmail: "",
  userPhone: "",
  levelName: "",
  levelId: 0,
  canSendCommand: false,
  canEdit: false,
};

const normalizePermissionFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    return (
      normalized === "S" ||
      normalized === "Y" ||
      normalized === "TRUE" ||
      normalized === "1"
    );
  }

  return Boolean(value);
};

const permissionPayloadValue = (value) => (normalizePermissionFlag(value) ? "S" : "N");

const normalizeVehicleItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    ...item,
    linked: normalizePermissionFlag(item.linked ?? item.Linked),
  }));
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function ClientUsers() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({ type: "title", value: "Usuários do Cliente" });
  }, [dispatch]);

  const [data, setData] = useState([]);

  const [formData, setFormData] = useReducer(formReducer, formInitialValues);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [levelAcessGroups, setLevelAcessGroups] = useState([]);
  const [userSessionData] = useState(storage.get(storageKeys.settings));
  // eslint-disable-next-line
  const [phone, setPhone] = useState("");
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicleLinks, setVehicleLinks] = useState([]);
  const [vehicleLinksOriginal, setVehicleLinksOriginal] = useState([]);
  const [selectedUserForVehicles, setSelectedUserForVehicles] = useState({});
  const [isVehicleModalLoading, setIsVehicleModalLoading] = useState(false);
  const [isVehicleModalSaving, setIsVehicleModalSaving] = useState(false);
  const [vehicleSearchFilter, setVehicleSearchFilter] = useState("");
  const [isLoadingEditUser, setIsLoadingEditUser] = useState(false);

  // Altura e paginação dinâmicos do grid (mesmo padrão de VehiclesNickame.jsx)
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;
  const BOTTOM_MARGIN = 70;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  let isEditing;

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";

    // Remove tudo que não é número
    const cleaned = phone.toString().replace(/\D/g, "");

    if (cleaned.length === 0) {
      return "";
    }

    // Aplica formatação baseada no tamanho
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    } else if (cleaned.length <= 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    } else {
      // 11 dígitos - celular: (11) 9 9999-9999
      const limited = cleaned.slice(0, 11);
      if (limited.length === 11) {
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 3)} ${limited.slice(3, 7)}-${limited.slice(7)}`;
      } else {
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
      }
    }
  };

  const handlePhoneKeyDown = (e) => {
    // Permite navegação e edição
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key)) {
      return;
    }

    // Permite apenas números
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "userPhone") {
      // Remove caracteres não numéricos
      const cleaned = value.replace(/\D/g, "");

      // Limita a 11 dígitos
      const limited = cleaned.slice(0, 11);

      setFormData({
        name,
        value: limited,
      });
    } else if (type === "checkbox") {
      setFormData({
        name,
        value: checked,
      });
    } else {
      setFormData({
        name,
        value: name === "levelId" ? parseInt(value) : value,
      });
    }
  };

  useEffect(() => {
    setPhone(formatPhoneNumber(formData.userPhone || "")); // Garante que nunca seja undefined
  }, [formData.userPhone]);

  const validateForm = () => {
    let newErrors = {};

    if (!formData?.name?.trim() && !editingItem.userName)
      newErrors.name = "Nome é obrigatório.";

    if (!formData?.email?.trim() && !editingItem.userEmail)
      newErrors.email = "E-mail é obrigatório.";

    const cleanedPhone = (formData.userPhone ?? "").replace(/\D/g, "");
    if (!cleanedPhone) {
      newErrors.userPhone = "Telefone é obrigatório.";
    } else if (cleanedPhone.length < 10 || cleanedPhone.length > 11) {
      newErrors.userPhone = "Telefone inválido.";
    }

    if (!formData?.levelId || formData.levelId === 0) {
      // 🔹 Valida se levelId existe e não é 0
      newErrors.levelId = "Nível de acesso é obrigatório.";
    }

    return newErrors;
  };

  // GET /v3/users/by-client/{userId}/{levelId} — resposta real em
  // camelCase: { licenseeId, levelId, levelName, userId, userName,
  // userEmail, userPhone, integrationKey, commandPermission,
  // editPermission }. Mantém os fallbacks PascalCase/canSendCommand-
  // canEdit por segurança, mas os nomes que realmente vêm são esses.
  const mapUserResponse = (item) => {
    const integrationKey = item.integrationKey ?? item.IntegrationKey ?? "";

    return {
      ...item,
      userId: item.userId ?? item.UserId,
      userName: item.userName ?? item.UserName,
      userEmail: item.userEmail ?? item.UserEmail,
      userPhone: formatPhoneNumber(item.userPhone ?? item.UserPhone),
      levelId: item.levelId ?? item.LevelId,
      levelName: item.levelName ?? item.LevelName,
      integrationKey:
        integrationKey === "00000000-0000-0000-0000-000000000000" ? "" : integrationKey,
      canSendCommand: normalizePermissionFlag(
        item.canSendCommand ??
          item.CanSendCommand ??
          item.commandPermission ??
          item.CommandPermission
      ),
      canEdit: normalizePermissionFlag(
        item.canEdit ?? item.CanEdit ?? item.editPermission ?? item.EditPermission
      ),
    };
  };

  const onEdit = async (item) => {
    setEditingItem(item);
    setErrors({});
    setShowForm(true);
    setIsLoadingEditUser(true);

    try {
      // Busca o dado do usuário clicado pelo endpoint — userId e levelId
      // (do próprio item da grid) só identificam QUEM buscar; o
      // formulário é preenchido só com o que essa busca devolver, nunca
      // com os dados já carregados na grid.
      const response = await usersService.byClientAndLevel(item.userId, item.levelId);

      // Só trata como erro (fecha o formulário e mostra o toast) quando a
      // API responde 404/400 de verdade. Qualquer resposta com sucesso
      // (200) é usada como veio, mesmo que algum campo venha vazio.
      if (!response || response?.error) {
        if (response?.isClientError) {
          setShowToast(true);
        }
        setShowForm(false);
        return;
      }

      const userData = mapUserResponse(response);

      setEditingItem(userData);

      setFormData({
        name: "userId",
        value: userData.userId,
      });

      setFormData({
        name: "name",
        value: userData.userName,
      });

      setFormData({
        name: "email",
        value: userData.userEmail,
      });

      setFormData({
        name: "userPhone",
        value: (userData.userPhone ?? "").toString().replace(/\D/g, ""),
      });

      setFormData({
        name: "levelId",
        value: userData.levelId ?? 0,
      }); // 🔹 Sincroniza o nível ao editar

      setFormData({
        name: "canSendCommand",
        value: userData.canSendCommand,
      });

      setFormData({
        name: "canEdit",
        value: userData.canEdit,
      });
    } finally {
      setIsLoadingEditUser(false);
    }
  };

  const onNew = () => {
    setFormData({
      reset: true,
    });
    setShowForm(true);
    setEditingItem({});
    setErrors({});
  };

  const onCancel = () => {
    setFormData({
      reset: true,
    });
    setEditingItem({});
    setShowForm(false);
    setErrors({});
  };

  const onDelete = (item) => {
    setIsDeleting({
      show: true,
      itemName: item.userName,
      itemRemoveId: item.userId,
    });
    setEditingItem(item);
  };

  const handleRemove = async (id) => {
    if (id) {
      try {
        setIsSaving(true);

        await clientUserService.updateStatus(id, 2);
        initialize();
      } catch (error) {
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsSaving(false);
          setShowForm(false);
          setShowToast(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
          handleCloseModal();
        }, 1000);
      }
    }
  };

  const onCloseToast = () => {
    setShowToast(false);
  };

  const onSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSaving(false); // 🔹 Garante que o loading pare
      return;
    }

    if (!formData.name && !formData.email && !formData.userPhone) {
      setShowToast(true);
      setIsSaving(false); // 🔹 Garante que o loading pare
      return;
    }

    setIsSaving(true);
    isEditing = editingItem?.userId > 0;

    const payload = {
      Username: formData.name,
      UserEmail: formData.email,
      UserCellPhone: formData.userPhone,
      UserLevelId: formData.levelId || 0, // 🔹 Usa o levelId do formData
      CommandPermission: permissionPayloadValue(formData.canSendCommand),
      EditPermission: permissionPayloadValue(formData.canEdit),
      canSendCommand: formData.canSendCommand,
      canEdit: formData.canEdit,
    };

    const payloadUpdate = {
      Username: formData.name,
      UserEmail: formData.email,
      UserCellPhone: formData.userPhone,
      UserTargetId: editingItem.userId,
      UserLevelId: formData.levelId || 0, // 🔹 Adiciona levelId ao update
      StatusId: 1,
      CommandPermission: permissionPayloadValue(formData.canSendCommand),
      EditPermission: permissionPayloadValue(formData.canEdit),
      canSendCommand: formData.canSendCommand,
      canEdit: formData.canEdit,
    };

    try {
      let response = isEditing
        ? await clientUserService.update(payloadUpdate)
        : await clientUserService.create(payload);

      if (!response) {
        setShowToast(true);
        setIsSaving(false); // 🔹 Evita o loading infinito
        return;
      }
    } catch (error) {
      setShowToast(true);
    } finally {
      setTimeout(() => {
        setIsSaving(false);
        setShowForm(false);
        setShowToast(false);
        setFormData({ reset: true });
        setEditingItem({});
        setErrors({});
        initialize();
      }, 1000);
    }
  };

  // usersService.byClient — cada linha vem como { userId, userName,
  // levelName, levelId, email, mobile, childCommand, childEdit,
  // integrationKey }. Mapeia pros nomes que o resto da tela usa
  // (userEmail, userPhone, canSendCommand, canEdit), aceitando também os
  // nomes antigos/PascalCase de defesa.
  const mapByClientUser = (item) => {
    const integrationKey = item.integrationKey ?? item.IntegrationKey ?? "";

    return {
      ...item,
      userId: item.userId ?? item.UserId,
      userName: item.userName ?? item.UserName,
      userEmail: item.email ?? item.Email ?? item.userEmail ?? item.UserEmail ?? "",
      userPhone: formatPhoneNumber(
        item.mobile ?? item.Mobile ?? item.userPhone ?? item.UserPhone
      ),
      levelId: item.levelId ?? item.LevelId,
      levelName: item.levelName ?? item.LevelName,
      integrationKey:
        integrationKey === "00000000-0000-0000-0000-000000000000" ? "" : integrationKey,
      canSendCommand: normalizePermissionFlag(
        item.childCommand ??
          item.ChildCommand ??
          item.canSendCommand ??
          item.CanSendCommand ??
          item.commandPermission ??
          item.CommandPermission
      ),
      canEdit: normalizePermissionFlag(
        item.childEdit ??
          item.ChildEdit ??
          item.canEdit ??
          item.CanEdit ??
          item.editPermission ??
          item.EditPermission
      ),
    };
  };

  const initialize = async () => {
    setIsPageLoading(true);
    try {
      const clientId = userSessionData?.clientId;
      if (!clientId) {
        setErrorMessage("Nenhum registro encontrado");
        setData([]);
        return;
      }

      const response = await usersService.byClient(clientId);

      if (!response || (Array.isArray(response) && response.length === 0)) {
        setErrorMessage("Nenhum registro encontrado");
        setData([]);
        return;
      }

      const formattedResponse = Array.isArray(response)
        ? response.map(mapByClientUser)
        : mapByClientUser(response);

      setData(formattedResponse);
      setErrorMessage("");
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setErrorMessage("Erro ao carregar usuários");
    } finally {
      setIsPageLoading(false);
    }
  };

  const openVehicleModal = async (item) => {
    setSelectedUserForVehicles(item);
    setShowVehicleModal(true);
    setIsVehicleModalLoading(true);
    setVehicleSearchFilter("");

    try {
      const response = await clientUserService.fetchUserSonVehicles(item.userId);
      const normalizedVehicles = normalizeVehicleItems(response);
      setVehicleLinks(normalizedVehicles);
      setVehicleLinksOriginal(normalizedVehicles.map((vehicle) => ({ ...vehicle })));
    } catch (error) {
      setVehicleLinks([]);
      setVehicleLinksOriginal([]);
    } finally {
      setIsVehicleModalLoading(false);
    }
  };

  const updateVehicleLink = (vehicleId, linked) => {
    setVehicleLinks((currentVehicles) =>
      currentVehicles.map((vehicle) =>
        Number(vehicle.vehicleId) === Number(vehicleId)
          ? { ...vehicle, linked }
          : vehicle
      )
    );
  };

  const handleSelectAllVehicles = () => {
    setVehicleLinks((currentVehicles) =>
      currentVehicles.map((vehicle) => ({ ...vehicle, linked: true }))
    );
  };

  const handleDeselectAllVehicles = () => {
    setVehicleLinks((currentVehicles) =>
      currentVehicles.map((vehicle) => ({ ...vehicle, linked: false }))
    );
  };

  const filteredVehicleLinks = vehicleLinks.filter((vehicle) => {
    const search = vehicleSearchFilter.trim().toUpperCase();
    if (!search) return true;

    const plate = (vehicle.licensePlate ?? vehicle.plate ?? "").toString().toUpperCase();
    const plateTooltip = (vehicle.plateTooltip ?? vehicle.PlateTooltip ?? "").toString().toUpperCase();

    return plate.includes(search) || plateTooltip.includes(search);
  });

  const handleSaveVehicleLinks = async () => {
    if (!selectedUserForVehicles?.userId) {
      return;
    }

    setIsVehicleModalSaving(true);

    try {
      const originalMap = new Map(
        vehicleLinksOriginal.map((vehicle) => [Number(vehicle.vehicleId), normalizePermissionFlag(vehicle.linked)])
      );

      const changes = vehicleLinks.filter((vehicle) => {
        const originalLinked = originalMap.get(Number(vehicle.vehicleId));
        return originalLinked !== normalizePermissionFlag(vehicle.linked);
      });

      if (changes.length > 0) {
        const commandPermission = permissionPayloadValue(
          selectedUserForVehicles.canSendCommand ??
            selectedUserForVehicles.commandPermission ??
            selectedUserForVehicles.CommandPermission
        );

        await Promise.all(
          changes.map((vehicle) =>
            clientUserService.setUserSonVehicleLink({
              userSonId: selectedUserForVehicles.userId,
              vehicleId: vehicle.vehicleId,
              linked: permissionPayloadValue(vehicle.linked),
              commandPermission,
            })
          )
        );
      }

      setShowVehicleModal(false);
      setVehicleLinks([]);
      setVehicleLinksOriginal([]);
      setSelectedUserForVehicles({});
      initialize();
    } catch (error) {
      setShowToast(true);
    } finally {
      setIsVehicleModalSaving(false);
    }
  };

  useEffect(() => {
    const fetchLevels = async () => {
      const userLogId = userSessionData?.userLogId;

      if (!userLogId) return;

      try {
        const response = await clientUserService.getUserLevels(userLogId); // 🔹 Chamada à API
        if (response) {
          setLevelAcessGroups(response || []); // 🔹 Armazenando os dados no estado
        }
      } catch (error) {
        console.error("Erro ao buscar níveis de acesso:", error);
      }
    };
    fetchLevels();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line
  }, []);

  // Debounce simples para resize + recálculo ao terminar loading (mesmo
  // padrão de VehiclesNickame.jsx)
  useEffect(() => {
    if (!isPageLoading) paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT, MIN_PAGE,
      MAX_PAGE,
      data,
      window.innerHeight,
      setPageSize
    );

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT, MIN_PAGE,
        MAX_PAGE,
        data,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoading]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastError show={showToast} closeToast={onCloseToast} />

      <DeleteModal
        itemName={isDeleting.itemName}
        itemRemoveId={isDeleting.itemRemoveId}
        callBackRemove={handleRemove}
        show={isDeleting.show}
        handleClose={handleCloseModal}
      />

      {isPageLoading ? (
        <Spinner />
      ) : (
        <div
          className="card"
          style={showForm || isPageLoading ? { display: "none" } : { display: "" }}
        >
          <div className="d-flex justify-content-between">
            <div className="p-2 card-header">
              <h5>Clientes cadastrados</h5>
            </div>
            <div className="p-2"></div>
            <div className="p-2">
              <SiAddthis className="icon-new" onClick={onNew} />
            </div>
          </div>

          <div ref={gridAnchorRef} />

          <GridComponent
              dataSource={data}
              allowPaging={true}
              allowFiltering={true}
              filterSettings={{ type: "Menu" }}
              pageSettings={{ pageSize }}
              rowHeight={ROW_HEIGHT}
              height={"auto"}
              style={{ marginTop: 16 }}
              locale="pt-BR"
              emptyRecordTemplate={() => <EmptyData />}
              actionComplete={() => {
                setTimeout(() => paginationService.calculateGridMetrics(
                  gridAnchorRef,
                  BOTTOM_MARGIN,
                  HEADER_PAGER_BUDGET,
                  ROW_HEIGHT, MIN_PAGE,
                  MAX_PAGE,
                  data,
                  window.innerHeight,
                  setPageSize
                ), 50);
              }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="userName"
                  headerText="Nome"
                  width="160"
                />
                <ColumnDirective
                  field="userPhone"
                  headerText="Telefone"
                  width="140"
                />
                <ColumnDirective
                  field="userEmail"
                  headerText="Email"
                  width="220"
                />
                <ColumnDirective
                  field="levelName"
                  headerText="Nível de Acesso"
                  width="160"
                />
                <ColumnDirective
                  headerText="Pode Enviar Comando"
                  width="130"
                  textAlign="Center"
                  template={(props) =>
                    props.canSendCommand ? (
                      <span title="Sim" style={{ color: "#198754" }}>
                        <FaCheck />
                      </span>
                    ) : (
                      <span title="Não" style={{ color: "#dc3545" }}>
                        <FaTimes />
                      </span>
                    )
                  }
                />
                <ColumnDirective
                  headerText="Pode Editar"
                  width="100"
                  textAlign="Center"
                  template={(props) =>
                    props.canEdit ? (
                      <span title="Sim" style={{ color: "#198754" }}>
                        <FaCheck />
                      </span>
                    ) : (
                      <span title="Não" style={{ color: "#dc3545" }}>
                        <FaTimes />
                      </span>
                    )
                  }
                />
                <ColumnDirective
                  field="integrationKey"
                  headerText="Chave"
                  width="220"
                />
                <ColumnDirective
                  headerText="Ações"
                  width="120"
                  template={(props) => (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <FaCar
                        className="icon-button"
                        onClick={() => openVehicleModal(props)}
                      />
                      <BiEditAlt
                        className="icon-button"
                        onClick={() => onEdit(props)}
                      />
                      <BiTrash
                        className="icon-button"
                        onClick={() => onDelete(props)}
                      />
                    </div>
                  )}
                />
              </ColumnsDirective>
              <Inject services={[Page, Filter]} />
            </GridComponent>

          {errorMessage && <p className="mt-4">{errorMessage}</p>}
        </div>
      )}

      <Modal
        show={showVehicleModal}
        onHide={() => {
          if (!isVehicleModalSaving) {
            setShowVehicleModal(false);
            setVehicleLinks([]);
            setVehicleLinksOriginal([]);
            setSelectedUserForVehicles({});
          }
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!isVehicleModalSaving}>
          <Modal.Title>Vincular / Desvincular Veículos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isVehicleModalLoading ? (
            <div className="text-center py-4">Carregando veículos...</div>
          ) : vehicleLinks.length === 0 ? (
            <EmptyData />
          ) : (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-wrap gap-2 justify-content-between">
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={handleSelectAllVehicles}
                  disabled={isVehicleModalSaving}
                >
                  Selecionar Todos
                </Button>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={handleDeselectAllVehicles}
                  disabled={isVehicleModalSaving}
                >
                  Desmarcar Todos
                </Button>
              </div>

              <input
                type="text"
                className="form-control"
                placeholder="Buscar por placa ou tipo..."
                value={vehicleSearchFilter}
                onChange={(e) => setVehicleSearchFilter(e.target.value)}
                disabled={isVehicleModalSaving}
              />

              {filteredVehicleLinks.length === 0 ? (
                <EmptyData />
              ) : (
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                {filteredVehicleLinks.map((vehicle) => (
                  <div
                    key={vehicle.vehicleId}
                    className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
                  >
                    <div>
                      <strong>{vehicle.licensePlate ?? vehicle.plate ?? "Veículo"}</strong>
                      <div style={{ fontSize: "0.637rem", color: "#6c757d" }}>
                        {vehicle.plateTooltip ?? vehicle.PlateTooltip ?? "—"}
                      </div>
                    </div>
                    <Form.Check
                      type="checkbox"
                      checked={normalizePermissionFlag(vehicle.linked)}
                      onChange={(e) => updateVehicleLink(vehicle.vehicleId, e.target.checked)}
                      disabled={isVehicleModalSaving}
                    />
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              if (!isVehicleModalSaving) {
                setShowVehicleModal(false);
                setVehicleLinks([]);
                setVehicleLinksOriginal([]);
                setSelectedUserForVehicles({});
              }
            }}
            disabled={isVehicleModalSaving}
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleSaveVehicleLinks}
            disabled={isVehicleModalLoading || isVehicleModalSaving}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        className="row py-3"
        style={
          !showForm || isPageLoading ? { display: "none" } : { display: "block" }
        }
      >
        <div className="col-6">
          <div className="card">
            <h5 className="card-header">
              {editingItem?.userId > 0
                ? "Editar Usuário"
                : "Criar Novo Usuário"}
            </h5>
            <div className="card-header">
              {isLoadingEditUser ? (
                <Spinner />
              ) : (
              <>
              {editingItem?.userId > 0 ? (
                <div className="row mb-3">
                  <label
                    className="col-sm-3 col-form-label"
                    htmlFor="basic-default-name"
                  >
                    ID
                  </label>
                  <div className="col-sm-4">
                    <label
                      className="col-form-label"
                      htmlFor="basic-default-name"
                    >
                      {editingItem?.userId}
                    </label>
                  </div>
                  <div className="row">
                    <label
                      className="col-sm-3 col-form-label"
                      htmlFor="basic-default-name"
                    >
                      Chave de acesso
                    </label>
                    <div className="col-sm-8">
                      <label
                        className="col-form-label"
                        htmlFor="basic-default-name"
                      >
                        {editingItem?.integrationKey}
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                ""
              )}

              <input
                type="hidden"
                value={formData.infoCar || ""}
                id="infoCar"
                name="infoCar"
              />

              {/* Nome */}
              <div className="row mb-3">
                <label
                  className="col-sm-3 col-form-label"
                  htmlFor="basic-default-name"
                >
                  Nome
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    name="name"
                    placeholder={editingItem?.userName ?? "Nome"}
                    value={formData.name || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
              </div>

              {/* Telefone */}
              <div className="row mb-3">
                <label
                  className="col-sm-3 col-form-label"
                  htmlFor="basic-default-phone"
                >
                  Telefone
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      errors.userPhone ? "is-invalid" : ""
                    }`}
                    name="userPhone"
                    autoComplete="off"
                    placeholder={editingItem?.userPhone ?? "Telefone"}
                    value={formatPhoneNumber(formData.userPhone)}
                    onChange={handleChange}
                    onKeyDown={handlePhoneKeyDown}
                    maxLength={16}
                  />
                  {errors.userPhone && (
                    <div className="invalid-feedback">{errors.userPhone}</div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="row mb-3">
                <label
                  className="col-sm-3 col-form-label"
                  htmlFor="basic-default-email"
                >
                  Email
                </label>
                <div className="col-sm-9">
                  <input
                    type="email"
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    name="email"
                    placeholder={editingItem?.userEmail ?? "Email"}
                    value={formData.email || ""}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>

              {/* Nível de Acesso */}
              <div className="row mb-3">
                <label
                  className="col-sm-3 col-form-label"
                  htmlFor="basic-default-level"
                >
                  Nível de Acesso
                </label>
                <div className="col-sm-9">
                  <select
                    name="levelId"
                    id="basic-default-level"
                    className={`form-control ${
                      errors.levelId ? "is-invalid" : ""
                    }`}
                    onChange={handleChange}
                    value={formData.levelId || 0}
                  >
                    <option value="0">-- Selecione um nível --</option>
                    {levelAcessGroups.length > 0 &&
                      levelAcessGroups.map((item) => (
                        <option key={item.levelId} value={item.levelId}>
                          {item.levelName}
                        </option>
                      ))}
                  </select>
                  {errors.levelId && (
                    <div className="invalid-feedback">{errors.levelId}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-3 col-form-label">Pode Enviar Comando</div>
                <div className="col-sm-9 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    name="canSendCommand"
                    checked={Boolean(formData.canSendCommand)}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-3 col-form-label">Pode Editar</div>
                <div className="col-sm-9 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    name="canEdit"
                    checked={Boolean(formData.canEdit)}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Botões */}
              <div
                className="row justify-content-end"
                style={{ textAlign: "end" }}
              >
                <div className="col-ls-8 col-md-8 col-xs-12 mb-3">
                  <button
                    type="button"
                    className="btn btn-warning"
                    style={{ marginRight: "10px" }}
                    onClick={onCancel}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    Salvar
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
